import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonDropdown, Icon, Input, Popover, Select, SpaceBetween } from '@amzn/awsui-components-react';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import FormulaExpressionToolbar from 'src/components/shared/FormulaExpressionToolbar';
import { renderExpressionParts } from 'src/components/shared/FormulaExpressionFunctionComponents';
import ATPTextAreaAutoComplete, { AutoSuggesstListItem } from 'src/components/shared/ATPTextAreaAutoComplete';
import DomUtils from 'src/utils/DomUtils';
import TPEAction from 'src/models/common/TPEAction';
import {
    CommonBalancePoolColumn,
    CommonBalancePoolColumnDataType,
} from 'src/models/multidimensional-allocation/CommonBalancePool';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import { extractFormulaReferences, toExpressionParts } from './cbpFormula';
import 'src/assets/styles/react-table.scss';
// Styles the `@`-autocomplete popup (opaque background + border/shadow) — the same stylesheet the
// calculation-steps builder uses (globally available). With TPEReactTable there is no overflow-scrolled
// wrapper, so the popup renders at the caret and is not clipped by a row boundary.
import 'src/components/calculation-builder/calculation-steps/text-area-autocomplete.css';


// Functions offered in the toolbar's "Functions" dropdown; each inserts its call prefix.
const CBP_FUNCTIONS = ['safeDivide(', 'SUM(', 'COUNT(', 'MIN(', 'MAX(', 'ROUND(', 'ABS('];

// Result type the user picks per calculated column. Storage is always numeric; PERCENTAGE only
// changes how the value renders (×100 + "%"). Currency is intentionally omitted here.
const RESULT_TYPE_OPTIONS = [
    { label: 'Number', value: 'NUMBER' },
    { label: 'Percentage', value: 'PERCENTAGE' },
];
const resultTypeLabel = (dataType?: string) =>
    RESULT_TYPE_OPTIONS.find((o) => o.value === dataType)?.label ?? 'Number';

// One calc column formatted for the grid; carries the per-row edit flag + precomputed formula pills.
type CbpGridRow = CommonBalancePoolColumn & {
    seq: number;
    isBeingEdited: boolean;
    expressionParts: React.ReactNode;
};

const contextMenuActions = [
    { id: 'edit', text: 'Edit' },
    { id: 'duplicate', text: 'Duplicate' },
    { id: 'remove', text: 'Remove' },
];

/**
 * Inline editable cell (mirrors AllocationGroupFormulasGrid's EditableCell): when the row is being
 * edited the name is an Input and the formula is the shared `@`-autocomplete editor; otherwise the
 * name is plain text and the formula renders as blue token pills. Type is always "Calculated".
 */
const CbpEditableCell = ({
    value: initialValue,
    row: { index, original },
    column: { id },
    onRowUpdated,
    editorsData,
}: { value: any; row: any; column: any; onRowUpdated: any; editorsData: any }) => {
    const [value, setValue] = useState(initialValue);
    useEffect(() => setValue(initialValue), [initialValue]);

    const row = original as CbpGridRow;
    if (row.isBeingEdited) {
        switch (id) {
            case 'displayName':
                return (
                    <Input
                        className="cbpCalcNameEditor"
                        placeholder="Column name"
                        value={value || ''}
                        onChange={(e) => { setValue(e.detail.value); onRowUpdated(index, 'displayName', e.detail.value); }}
                        onBlur={() => onRowUpdated(index, 'displayName', value)}
                    />
                );
            case 'formula':
                return (
                    <ATPTextAreaAutoComplete
                        key={`formula_${row.columnId}`}
                        editorsData={editorsData}
                        initialValue={initialValue || ''}
                        onTextAreaValueChanged={editorsData.onTextAreaValueChanged}
                        onTextAreaBlur={(args: any) => onRowUpdated(index, 'formula', args.textAreaValue)}
                    />
                );
            case 'dataType':
                return (
                    <Select
                        expandToViewport
                        selectedOption={RESULT_TYPE_OPTIONS.find((o) => o.value === (value || 'NUMBER')) ?? RESULT_TYPE_OPTIONS[0]}
                        options={RESULT_TYPE_OPTIONS}
                        onChange={({ detail }) => {
                            setValue(detail.selectedOption.value);
                            onRowUpdated(index, 'dataType', detail.selectedOption.value);
                        }}
                    />
                );
        }
    } else if (id === 'formula') {
        return (
            <div className="cbpFormulaOverviewCell">
                {row.formula
                    ? row.expressionParts
                    : <Box variant="span" color="text-body-secondary">Click to enter a row-level formula. Type @ to insert a field.</Box>}
            </div>
        );
    } else if (id === 'displayName') {
        return <span>{value || <Box variant="span" color="text-body-secondary">Unnamed</Box>}</span>;
    } else if (id === 'dataType') {
        return <Box variant="span">{resultTypeLabel(value)}</Box>;
    }
    return <span>{value}</span>;
};

const cbpDefaultCellEditor = { Cell: CbpEditableCell };

/**
 * Step 2 — Configure calculated columns, built like the TP Allocation "Configure allocation groups"
 * grid (see AllocationGroupFormulasGrid): the Operations palette is pinned on top, rows are edited
 * INLINE via TPEReactTable's editable cells (name Input + `@`-autocomplete formula), and formula
 * references render as blue token pills. "Save" persists via UpdateCommonBalancePool.
 */
export default function CbpCalculatedColumnsSection() {
    const { cbpBuilderState, cbpBuilderDispatch, canEdit, persistColumns } = useContext(CommonBalancePoolBuilderContext);
    const { pool, saving } = cbpBuilderState;
    // All pool columns (inputs + calc). Memoised so `working`'s sync effect keys on a stable reference.
    const columns = useMemo(() => pool?.columns ?? [], [pool]);
    // Working copy of ALL columns; inline edits mutate it, "Save" persists it.
    const [working, setWorking] = useState<CommonBalancePoolColumn[]>(columns);
    const [editingId, setEditingId] = useState<string | undefined>(undefined);
    // Caret lives in a ref (not state) so caret moves don't re-render and remount the textarea.
    const caretRef = useRef(0);
    // Live formula text of the edited row. The shared editor reports on change (not just on blur), so
    // palette/function inserts operate on the current text instead of the last-blurred value.
    const liveFormulaRef = useRef('');
    // Monotonic counter guarantees unique column ids even for rows created in the same millisecond.
    const idCounter = useRef(0);
    const nextColumnId = useCallback(() => `calc_${Date.now()}_${idCounter.current += 1}`, []);
    const [skipPageReset, setSkipPageReset] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);

    // Pull in external column updates (e.g. after a successful save) when not mid-edit.
    useEffect(() => {
        if (editingId == null) setWorking(columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    // Seed the live-formula buffer with the edited row's current formula whenever editing starts, and
    // reset the caret so a palette/function insert done before the user clicks into the (remounted)
    // editor lands at the row's end rather than the previously edited row's stale offset.
    useEffect(() => {
        liveFormulaRef.current = working.find((c) => c.columnId === editingId)?.formula ?? '';
        caretRef.current = liveFormulaRef.current.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingId]);

    // Report unsaved-edit state up so the builder can guard its top-level lifecycle actions
    // (Save draft / Submit / Execute persist the pool's columns, not this section's working copy).
    const isDirty = editingId != null || JSON.stringify(working) !== JSON.stringify(columns);
    useEffect(() => {
        cbpBuilderDispatch(ACTIONS.SET_STEP2_DIRTY.withPayload(isDirty));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    const calcColumns = working.filter((c) => c.kind === 'CALC');
    const nameById: Record<string, string> = Object.fromEntries(
        working.map((c) => [c.columnId, c.displayName || c.columnId]),
    );

    // Fields a formula may reference: inputs + other calc columns (never the one being edited).
    const suggestionsList: AutoSuggesstListItem[] = working
        .filter((c) => c.kind === 'INPUT' || (c.kind === 'CALC' && c.columnId !== editingId))
        .map((c) => ({
            name: c.displayName || c.columnId,
            value: c.columnId,
            type: c.kind === 'INPUT' ? 'Input columns' : 'Calculated columns',
        }));

    const updateCol = (columnId: string, patch: Partial<CommonBalancePoolColumn>) =>
        setWorking((cols) => cols.map((c) => (c.columnId === columnId ? { ...c, ...patch } : c)));

    // Toolbar (reused FormulaExpressionToolbar) dispatches APPEND_TO_FORMULA_EDITOR; insert at the caret.
    const insertAtCaret = (text: string) => {
        if (!editingId) return;
        // Use the live (possibly unblurred) formula, not the last-committed value in `working`.
        const f = liveFormulaRef.current;
        const pos = Math.min(caretRef.current, f.length);
        const nextFormula = f.slice(0, pos) + text + f.slice(pos);
        liveFormulaRef.current = nextFormula;
        updateCol(editingId, { formula: nextFormula });
        caretRef.current = pos + text.length;
    };
    const toolbarDispatch = (action: TPEAction) => {
        if (action.type === 'APPEND_TO_FORMULA_EDITOR') insertAtCaret(action.payload as string);
    };

    const startAdd = () => {
        const columnId = nextColumnId();
        setWorking((cols) => [
            ...cols,
            { columnId, displayName: '', kind: 'CALC', dataType: 'NUMBER' as CommonBalancePoolColumnDataType, formula: '' },
        ]);
        setEditingId(columnId);
        caretRef.current = 0;
        setValidationError(undefined);
    };

    // Stable callbacks (functional setState only) so the memoised columns never go stale.
    // Fresh monotonic columnId so a duplicate never collides (even in the same millisecond) — a
    // collision would make updateCol / nameById / validation key onto multiple rows.
    const handleDuplicate = useCallback((col: CommonBalancePoolColumn) => {
        // col arrives from the grid as a CbpGridRow; drop the grid-only fields (seq / isBeingEdited /
        // expressionParts — the last is a React node that isn't JSON-serializable) before cloning, so they
        // don't leak into `working` (which the dirty check stringifies) or the persisted payload.
        const { seq, isBeingEdited, expressionParts, ...domainCol } = col as CbpGridRow;
        void seq; void isBeingEdited; void expressionParts;
        setWorking((cols) => [...cols, { ...domainCol, columnId: nextColumnId(), displayName: `${domainCol.displayName} (copy)` }]);
    }, [nextColumnId]);

    const handleRemove = useCallback((col: CommonBalancePoolColumn) => {
        setWorking((cols) => cols.filter((c) => c.columnId !== col.columnId));
        setEditingId((cur) => (cur === col.columnId ? undefined : cur));
    }, []);

    const startEdit = useCallback((columnId: string) => setEditingId(columnId), []);

    // Exit edit mode WITHOUT persisting: revert the working copy to the last-saved columns and clear the
    // edited row (dropping a freshly-added, never-saved row along the way). Needed because entering edit
    // — clicking a row, or "Add calculated column" — sets step2Dirty, and the builder gates Save draft /
    // Submit / Execute / Add input on step2Dirty; without a non-persisting exit the only escapes are a
    // Save round-trip (which rejects an empty new row) or Remove, which wedges the builder.
    const handleCancelEdit = () => {
        setWorking(columns);
        setEditingId(undefined);
        setValidationError(undefined);
    };

    const handleSave = async () => {
        // Flush the actively-edited row's live formula (committed only on blur otherwise) so clicking
        // Save while the editor is still focused doesn't drop the last keystrokes.
        const effectiveWorking = editingId
            ? working.map((c) => (c.columnId === editingId ? { ...c, formula: liveFormulaRef.current } : c))
            : working;
        // Defense: reject duplicate column ids before persisting (they collapse DAG edges / refs).
        const dupId = effectiveWorking.map((c) => c.columnId).find((id, i, arr) => arr.indexOf(id) !== i);
        if (dupId) {
            setValidationError(`Duplicate column id "${dupId}". Remove or rename the duplicate.`);
            return;
        }
        const known = new Set(effectiveWorking.map((c) => c.columnId));
        const next: CommonBalancePoolColumn[] = [];
        for (const col of effectiveWorking) {
            if (col.kind !== 'CALC') {
                next.push(col);
                continue;
            }
            const name = (col.displayName || '').trim();
            const formula = (col.formula || '').trim();
            if (!name) {
                setValidationError('Every calculated column needs a name.');
                return;
            }
            if (!formula) {
                setValidationError(`"${name}" needs a formula.`);
                return;
            }
            const refs = extractFormulaReferences(formula);
            if (refs.includes(col.columnId)) {
                setValidationError(`"${name}" cannot reference itself.`);
                return;
            }
            const unknown = refs.filter((id) => !known.has(id));
            if (unknown.length > 0) {
                setValidationError(`"${name}" references unknown column(s): ${unknown.join(', ')}.`);
                return;
            }
            next.push({ ...col, displayName: name, formula, dataType: col.dataType || 'NUMBER', dependsOn: refs });
        }
        setValidationError(undefined);
        const persisted = await persistColumns(next);
        if (persisted) {
            // Re-sync the local copy on success: the `columns`-keyed effect skips while editingId is set,
            // and after we clear editingId the `columns` reference doesn't change again — so without this
            // `working` keeps its pre-save values and isDirty stays true, wedging the lifecycle actions.
            //
            // Sync to the columns the server actually persisted (what persistColumns wrote to
            // `pool.columns`), NOT the local `next`: the backend owns fields the builder does not send —
            // chiefly `columnOrder`, which the input modal sets but Step 2 never does, and which the read
            // path stamps and sorts by. Syncing to `next` would leave `working` ≠ `pool.columns`, so the
            // JSON dirty check stays true and step2Dirty never clears, wedging the lifecycle actions until
            // a full page reload.
            setWorking(persisted);
            setEditingId(undefined);
        }
    };

    // Cell edits arrive here as (rowIndex, field, value) where field is the react-table column id.
    const onRecordUpdated = (rowIndex: number, field: string, value: any) => {
        setSkipPageReset(true);
        const col = calcColumns[rowIndex];
        if (col) updateCol(col.columnId, { [field]: value });
    };

    // Clicking a row enters edit mode (ignore clicks on the row-actions menu).
    const onRowClicked = (e: any, original: CbpGridRow) => {
        if (!canEdit || saving) return;
        if (DomUtils.isDescendantOfClassName(e.target, 'cbpRowActions')) return;
        setEditingId(original.columnId);
    };

    const formattedRecords: CbpGridRow[] = calcColumns.map((c, i) => ({
        ...c,
        seq: i + 1,
        isBeingEdited: c.columnId === editingId,
        expressionParts: c.formula ? renderExpressionParts(toExpressionParts(c.formula, nameById)) : null,
    }));

    const columnDefinitions = React.useMemo(() => [
        {
            accessor: 'seq',
            Header: () => <div className="cell-text-non-sortable">#</div>,
            Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
        },
        {
            accessor: 'displayName',
            Header: () => <div className="cell-text-non-sortable">Column name</div>,
        },
        {
            accessor: 'dataType',
            Header: () => <div className="cell-text-non-sortable">Type</div>,
        },
        {
            accessor: 'formula',
            Header: (
                <div className="headerToggle">Formula overview &nbsp;
                    <Popover
                        dismissButton={false}
                        position="right"
                        size="small"
                        triggerType="custom"
                        content="Click the expression to edit. Type '@' to display the autocomplete menu."
                    >
                        <Icon name="status-info" size="small" variant="link" />
                    </Popover>
                </div>
            ),
        },
        {
            accessor: 'actions',
            Header: () => <div className="cell-text-non-sortable">Action</div>,
            Cell: (e: any) => (canEdit ? (
                <div className="cbpRowActions rowActions">
                    <ButtonDropdown
                        className="rowContextMenu gridContextMenu"
                        disabled={saving}
                        expandToViewport
                        items={contextMenuActions}
                        onItemClick={(bd) => {
                            const col = e.row.original as CommonBalancePoolColumn;
                            if (bd.detail.id === 'edit') startEdit(col.columnId);
                            else if (bd.detail.id === 'duplicate') handleDuplicate(col);
                            else if (bd.detail.id === 'remove') handleRemove(col);
                        }}
                    />
                </div>
            ) : null),
        },
    ], [canEdit, saving, startEdit, handleDuplicate, handleRemove]);

    return (
        <SpaceBetween direction="vertical" size="m">
            <Box color="text-body-secondary">
                Write a row-level formula for each calculated column — use the Operations palette and type <b>@</b> to
                reference an input or a prior calculated column. Formulas evaluate per grain row using the ATP formula engine.
            </Box>

            {/* Operations palette pinned on top (reused from the calculation-steps builder). */}
            {canEdit && (
                <FormulaExpressionToolbar dispatch={toolbarDispatch} showBuiltInFunctions builtInFunctions={CBP_FUNCTIONS} />
            )}

            {validationError && (
                <Alert type="error" dismissible header="Could not save calculated columns" onDismiss={() => setValidationError(undefined)}>
                    {validationError}
                </Alert>
            )}

            <TPEReactTable {...{
                data: formattedRecords,
                columnDefinitions,
                className: 'cbpCalcColumnsGrid',
                sortable: false,
                key: 'cbpCalcColumnsGrid',
                emptyMessage: canEdit
                    ? 'No calculated columns yet. Click "Add calculated column" to start.'
                    : 'No calculated columns.',
                onRowClicked,
                editableOptions: {
                    defaultColumn: cbpDefaultCellEditor,
                    onRowUpdated: onRecordUpdated,
                    skipPageReset,
                    editorsData: {
                        suggestionsList,
                        onTextAreaCaretPositionChanged: (position: number) => { caretRef.current = position; },
                        onTextAreaValueChanged: (v: string) => { liveFormulaRef.current = v; },
                    },
                },
            }} />

            {canEdit && (
                <div className="actionButtonsContainer">
                    <Box float="left">
                        <Button variant="normal" onClick={startAdd} disabled={saving}>Add calculated column</Button>
                    </Box>
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            {isDirty && (
                                <Button variant="normal" onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
                            )}
                            <Button variant="primary" onClick={handleSave} loading={saving}>Save</Button>
                        </SpaceBetween>
                    </Box>
                </div>
            )}
        </SpaceBetween>
    );
}
